import { Search, Settings } from '.generated/Feature.CW.model';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { tv } from 'tailwind-variants';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import {
  buildInteractiveResult,
  //loadAdvancedSearchQueryActions,
  Result,
  buildFacet,
} from '@coveo/headless';
import { Data } from '.generated/Foundation.CW.model';
import useDictionary from 'lib/hooks/useDictionary';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
export type LocationPageProps = Data.BaseTemplateSections.Location;
export type ProviderCardListingProps = Search.ProviderCardListing & {
  params: {
    Theme: string;
    maxNumberOfLocationCards: string;
  };
  isSidenavPart?: boolean;
};
export type PageSettings = Settings.SiteSettings['fields'];

const ProviderCardListing = (props: ProviderCardListingProps) => {
  //const fields = props?.fields;
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const fieldsToInclude = [
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwspecialties',
    'cwacceptingnewpatients',
    'cwphoto',
    'clickUri',
  ];
  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: contextWithSettings?.providerSearchHub?.value || '',
      pipeline: contextWithSettings?.providerPipeline?.value || '',
      accessToken: contextWithSettings?.providerAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [contextWithSettings]);

  if (!coveoConfig?.organizationId && !coveoConfig?.accessToken && !coveoConfig?.pipeline) {
    return <></>;
  }
  return (
    <CoveoEngineProvider config={coveoConfig} fieldsToInclude={fieldsToInclude}>
      <ComponentWrapper
        className="bg-theme-bg provider-listing-wrapper"
        isSidenavPart={props?.isSidenavPart}
      >
        <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10">
          <ProviderListingRenderer
            maxCards={parseInt(props?.params?.maxNumberOfLocationCards || '4')}
          />
        </div>
      </ComponentWrapper>
    </CoveoEngineProvider>
  );
};

export default ProviderCardListing;
const ProviderListingRenderer = ({ maxCards }: { maxCards: number }) => {
  const { ProviderProxyImage } = TAILWIND_VARIANTS();
  const clickedToggleRef = useRef(false);
  const engine = useCoveoEngine();
  const { getDictionaryValue } = useDictionary();
  const showAlldataLable = getDictionaryValue('ShowAllStaffLabel');
  const showLessdataLable = getDictionaryValue('ShowLess');
  const { ContextResults, setContextResults } = useSearch();
  const { sitecoreContext } = useSitecoreContext();
  const locationPageDetails = sitecoreContext?.route as LocationPageProps;
  const locationName = locationPageDetails.fields?.locationName?.value;
  const [showAll, setShowAll] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const sortResults = useCallback((results: Result[]): Result[] => {
    const normalize = (val?: string) => (val || '').trim().toLowerCase();
    return results.slice().sort((a, b) => {
      const aAccepting = normalize(a?.raw?.cwacceptingnewpatients as string);
      const bAccepting = normalize(b?.raw?.cwacceptingnewpatients as string);

      const aPriority = aAccepting.startsWith('accepting') ? 0 : 1;
      const bPriority = bAccepting.startsWith('accepting') ? 0 : 1;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      const aFirst = normalize(a?.raw?.cwfirstname as string);
      const bFirst = normalize(b?.raw?.cwfirstname as string);

      return aFirst.localeCompare(bFirst);
    });
  }, []);

  useEffect(() => {
    if (!engine) return;
    const normalizedLocation = locationName?.trim() || '';

    // Build facet controller and preselect location value before first search
    const facet = buildFacet(engine, {
      options: {
        field: 'cwlocationtitles',
        facetId: 'cwLocationFacet',
        numberOfValues: 50,
      },
    });

    if (normalizedLocation) {
      facet.deselectAll();
      facet.toggleSingleSelect({
        value: normalizedLocation,
        numberOfResults: 0,
        state: 'selected',
      });
    }

    // const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

    // engine.dispatch(
    //   updateAdvancedSearchQueries({
    //     //if your field is multi facet use query like this.
    //     // cq: `(@cwlocationtitles=@${locationName})`,
    //     cq: `@cwlocationtitles=="${locationName}"`,
    //   })
    // );

    engine.executeFirstSearch();

    const unsubscribe = engine.subscribe(() => {
      //   const results = engine.state.search.results;
      //   if (results && results.length > 0) {
      //     const sorted: Result[] = sortResults(results);
      //     setContextResults(sorted);
      //   }
      const fullSearchState = engine.state.search;
      if (fullSearchState.results?.length > 0) {
        const sortedResults = sortResults(fullSearchState.results);
        setContextResults({
          ...fullSearchState,
          results: sortedResults,
        });
      }
    });

    return () => unsubscribe();
  }, [engine, locationName, setContextResults, sortResults]);

  useEffect(() => {
    if (clickedToggleRef.current && !showAll && topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
      clickedToggleRef.current = false; // reset after scroll
    }
  }, [showAll]);

  //   const resultsToRender = ContextResults || [];
  //   const resultCount = resultsToRender.length;
  const resultsToRender = ContextResults?.results || [];
  const resultCount = ContextResults?.response?.totalCountFiltered;
  const visibleResults = showAll ? resultsToRender : resultsToRender.slice(0, maxCards);
  const shouldShowToggle = resultCount && resultCount > maxCards;

  return (
    <div
      ref={topRef}
      className="resultlist-wrapper"
      id="location-staff-list"
      data-hasresult={resultsToRender?.length > 0 ? 'true' : 'false'}
    >
      {visibleResults?.length > 0 ? (
        <div className="mt-10">
          <div>
            <ul className="mt-4 space-y-4">
              {visibleResults.map((item: Result, index) => {
                const degrees = item?.raw?.cwdegreetitles
                  ? (item?.raw?.cwdegreetitles as string).replaceAll(';', '/')
                  : '';
                const controller =
                  engine &&
                  buildInteractiveResult(engine, {
                    options: { result: item },
                  });
                return (
                  <>
                    <li
                      key={index}
                      className="py-[30px] flex flex-col lg:flex-row border-b-2 border-gray-50"
                    >
                      <div className="flex-grow">
                        <LinkWrapper
                          field={{ href: item?.clickUri }}
                          onClick={() => {
                            controller?.select();
                          }}
                        >
                          <h4 className="text-blue-base font-bold text-24 md:text-28 leading-31 hover:underline hover:text-darkblue-base active:text-darkblue-base underline-offset-[6px]">
                            {item.raw.cwfirstname as string} {item.raw.cwlastname as string}
                            {degrees && (
                              <span>
                                <span>{', '}</span>
                                <span>{degrees}</span>
                              </span>
                            )}
                          </h4>
                        </LinkWrapper>

                        {Array.isArray(item?.raw?.cwspecialties) && (
                          <span className="block my-5 font-normal text-18 text-gray-900">
                            <PlainTextWrapper
                              field={{
                                value: item.raw.cwspecialties
                                  .map((spec: string) => spec.trim())
                                  .join(', '),
                              }}
                            />
                          </span>
                        )}
                        <div className="block lg:hidden w-full md:min-w-[250px] md:w-[250px] mb-8">
                          {item.raw.cwphoto ? (
                            <ImageWrapper
                              className="w-[100px] min-h-[125px] md:w-[270px] md:h-[270px] rounded-[8px] object-cover"
                              field={{ value: { src: `${item.raw.cwphoto}` } }}
                            />
                          ) : (
                            <div className={ProviderProxyImage()}>
                              <SvgIcon
                                icon="user"
                                className="!h-[60px] !w-[60px] [&>path]:fill-darkblue-base"
                                size="lg"
                                aria-label="Provider Profile Video"
                              />
                            </div>
                          )}
                        </div>

                        {(item.raw.cwacceptingnewpatients as string) && (
                          <div
                            className={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'bg-utility-red-50' : 'bg-utility-green-50'} text-gray-900 font-nunitoSans text-18 font-normal leading-[31px] md:inline-flex flex md:items-center items-start lg:mt-16 mt-4 px-8 py-3 rounded-[6px] mb-12`}
                          >
                            <SvgIcon
                              icon={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'xcircle' : 'check-circle'}`}
                              className={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? '[&>path]:fill-utility-red-700' : '[&>path]:fill-utility-green-700'} mr-4 !h-[22px] !w-[22px]`}
                              size="lg"
                              aria-label={item.raw.cwacceptingnewpatients}
                            />
                            <span>{item.raw.cwacceptingnewpatients as string}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-x-24">
                        <div className="hidden lg:block max-w-[160px] w-full">
                          {item.raw.cwphoto ? (
                            <ImageWrapper
                              className="w-full min-h-[200px] rounded-[8px] object-cover"
                              field={{ value: { src: `${item.raw.cwphoto}` } }}
                            />
                          ) : (
                            <div className={ProviderProxyImage()}>
                              <SvgIcon
                                icon="user"
                                className="!h-[80px] !w-[80px] [&>path]:fill-darkblue-base"
                                size="lg"
                                aria-label="Provider Profile Video"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  </>
                );
              })}
            </ul>

            {/* Show All / Show Less CTA */}
            {shouldShowToggle && (
              <ButtonWrapper
                aria-expanded={showAll}
                ctaVariant="tertiary"
                onClick={() => {
                  clickedToggleRef.current = true; // set flag before changing state
                  setShowAll((prev) => !prev);
                }}
                className="px-7 py-4 font-centuryGothic text-18 leading-[25px] text-blue-base hover:bg-blue-50 hover:text-darkblue-base !rounded-[8px] focus:border-blue-base hover:no-underline bg-transparent flex items-center font-bold lg:mt-[40px] mt-[32px]"
              >
                {showAll
                  ? showLessdataLable
                  : showAlldataLable.replace('{num}', resultCount.toString())}
                <SvgIcon
                  icon={showAll ? 'minus' : 'plus'}
                  className="ml-4 w-[22px] h-[22px] mb-1 [&>path]:fill-current"
                  aria-label={showAll ? 'Collapse list' : 'Expand list'}
                />
              </ButtonWrapper>
            )}
          </div>
        </div>
      ) : (
        <>
          <div>No data found</div>
        </>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    ProviderProxyImage: [
      'bg-blue-100',
      'rounded-md',
      'mb-[26px]',
      'lg:w-[160px] lg:h-[200px]',
      'md:w-[160px] md:h-[200px]',
      'w-[100px] h-[125px]',
      'flex items-center justify-center',
    ],
  },

  variants: {},
});
